import React, { useState, useEffect, useRef } from 'react';
import ShopTheProject from '@digital-retail/shop-the-project';
import PropTypes from 'prop-types';
import { isSodimacStandalone } from '@digital-retail/store-manager';
import styles from './ShopTheProject.styles';
import { withProduct } from '../../utils/ProductContext';
import { withCart } from '../../utils/CartContext';
import { withApplicationContext } from '../../utils/ApplicationContext';
import CookieStorage from '../../utils/CookieStorage';
import _ from '../../utils/LodashImports';
import getLabels from './ShopTheProject.labels';
import { usePriceMapper } from '../../utils/hooks/usePriceMapper';
import { usePriceMapperV2 } from '../../utils/hooks/usePriceMapperV2';
import constants from '../../config/constants';
import { groupByBackendCategoryId } from '../../utils/RecoHelper';

const localStorageKey = 'shop-the-project-already-seen-by-user';
const slotsComplementaryProducts = 'PDP-PRJ-1';
const slotsSimilarProducts = 'PDP-PRJ-2';
const minComplementaryProducts = 3;
const maxComplementaryProducts = 5;
const maxSimilarProducts = 5;

const storeCodes = {
  [constants.STORES.sodimac]: 'SO',
  [constants.STORES.tottus]: 'TO',
  [constants.STORES.linio]: 'LI',
  [constants.STORES.tienda]: 'TI',
  [constants.STORES.homecenter]: 'SO',
  [constants.STORES.so_com]: 'SO',
  [constants.STORES.to_com]: 'TO',
};

const ShopTheProjectWrapper = ({ activeImageIndex, mainProductImage, appCtx, product, cart }) => {
  const projectNamePrimary = useRef(undefined);
  const projectNameSecondary = useRef(undefined);
  const complementImpression = useRef('');
  const similarImpressions = useRef({});
  const storeCode = storeCodes[appCtx.store];
  const storeAndCountryCode = `${storeCode}${appCtx.regionCode.toUpperCase()}`;
  const [showCoachmark, setShowCoachmark] = useState(undefined);
  const [initialPrimaryProducts, setInitialPrimaryProducts] = useState([]);
  const { mapPrices } = usePriceMapper();
  const { mapPricesV2 } = usePriceMapperV2();
  const labels = getLabels(appCtx);

  const fetchProducts = async ({ productId, variantId, slots, categoryId, webCategoryId, brandName, sellerId }) => {
    const contextId = CookieStorage.getCookie('contextID') || '';
    const site = appCtx.store;
    const channel = isSodimacStandalone(site) ? 'standalone_web' : 'web';
    const widgetsUUID = '';
    const userId = CookieStorage.getCookie('USER_ID') || '';

    const response = await product.loadRecommendedProducts({
      productId,
      zones: appCtx.zones,
      regionCode: appCtx.regionCode,
      channel,
      site,
      widgetsUUID,
      variantId,
      userId,
      slots,
      categoryId,
      webCategoryId,
      brandName,
      sellerId,
      contextId,
      politicalId: appCtx.politicalId,
      priceGroupId: appCtx.priceGroupId,
    });
    const name = _.get(response, '[0].name', undefined);
    const rawProducts = _.get(response, '[0].data', []);
    const products = rawProducts.map((fetchedProduct) => ({
      ...fetchedProduct,
      variantId: fetchedProduct.offeringId,
      price: mapPrices(fetchedProduct),
    }));
    return { name, products };
  };

  const mainProduct = {
    productId: product.id,
    variantId: product.currentVariant,
    displayName: product.name,
    brand: product.brandName,
    price: mapPricesV2(_.get(product, 'variants[0]', {})),
    url: window.location.href,
    sellerName: product.sellerInfo.sellerName,
    mediaUrls: [mainProductImage],
  };

  useEffect(() => {
    (async () => {
      const isShopTheProjectCoachmarkEnabled = _.get(
        appCtx,
        'siteConfig.toggles.isShopTheProjectCoachmarkEnabled',
        false
      );
      const isFirstRenderForThisUser = window?.localStorage?.getItem(localStorageKey) === null;
      setShowCoachmark(isShopTheProjectCoachmarkEnabled && isFirstRenderForThisUser);
      if (isFirstRenderForThisUser) window?.localStorage?.setItem(localStorageKey, true);

      const { name, products } = await fetchProducts({
        productId: product.id,
        variantId: product.currentVariant,
        slots: slotsComplementaryProducts,
        categoryId: _.get(product, 'attributes.GSCCategoryId', ''),
        webCategoryId: product.defaultParentCategoryId,
        brandName: product.brandName,
        sellerId: _.get(product, 'variants[0].offerings[0].sellerId', ''),
      });

      const primaryProducts = groupByBackendCategoryId({
        products,
        filterOptions: {
          maxGroups: maxComplementaryProducts,
          podsByGroup: 1,
          minPods: minComplementaryProducts,
        },
      });

      projectNamePrimary.current = name;
      setInitialPrimaryProducts(primaryProducts);
    })();

    const handleBeforeUnload = () => {
      if (complementImpression.current) {
        const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnImpressions', {
          detail: {
            value: complementImpression.current,
          },
        });
        window.dispatchEvent(eventToDigitalData);
      }
      Object.keys(similarImpressions.current).forEach((key) => {
        const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnImpressions', {
          detail: {
            value: similarImpressions.current[key],
          },
        });
        window.dispatchEvent(eventToDigitalData);
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = async (productsToAddToCart) => {
    const productsData = productsToAddToCart.map((productToAddToCart) => ({
      skuId: productToAddToCart.variantId,
      productId: productToAddToCart.productId,
      quantity: productToAddToCart.quantity,
      offeringId: productToAddToCart.variantId,
    }));
    return cart.addProduct({
      appCtx,
      products: productsData,
      product,
    });
  };

  const getSecondaryProducts = async (primaryProduct) => {
    const { name, products } = await fetchProducts({
      productId: primaryProduct.productId,
      variantId: primaryProduct.variantId,
      slots: slotsSimilarProducts,
      categoryId: '',
      webCategoryId: '',
      brandName: primaryProduct.brand,
      sellerId: primaryProduct.sellerName,
    });

    projectNameSecondary.current = name;

    return products.slice(0, maxSimilarProducts).map((secondaryProduct) => ({
      ...secondaryProduct,
      variantId: secondaryProduct.offeringId,
    }));
  };

  const onRender = () => {
    const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnRender', {
      detail: {
        value: `${projectNamePrimary.current}|${slotsComplementaryProducts}|${initialPrimaryProducts.length}`,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };
  const onClickOnCallToAction = (primaryProducts) => {
    const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnClickOnCallToAction', {
      detail: {
        value: `cr_recommendationstp!PDP!${storeAndCountryCode}!X!X!${projectNamePrimary.current}!${
          mainProduct.variantId
        }!X!${activeImageIndex + 1}!${primaryProducts.length}!${slotsComplementaryProducts}`,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };
  const onPrimaryProductImpression = (driverProduct, complementaryProduct) => {
    if (!complementImpression.current) {
      complementImpression.current = `${projectNamePrimary.current}-${driverProduct.variantId},${complementaryProduct.variantId}`;
      return;
    }
    complementImpression.current += `,${complementaryProduct.variantId}`;
  };
  const onSecondaryProductImpression = (complementaryProduct, similaryProduct) => {
    if (!similarImpressions.current[complementaryProduct.variantId]) {
      similarImpressions.current[
        complementaryProduct.variantId
      ] = `${projectNameSecondary.current}-${complementaryProduct.variantId},${similaryProduct.variantId}`;
      return;
    }
    similarImpressions.current[complementaryProduct.variantId] += `,${similaryProduct.variantId}`;
  };
  const onAddToCart = (primaryProductsOnCart, allPrimaryProducts) => {
    const variantIds = primaryProductsOnCart.map((primaryProductOncart) => primaryProductOncart.variantId);
    const positions = primaryProductsOnCart.map(
      (primaryProductOncart) =>
        allPrimaryProducts.findIndex((primaryProduct) => primaryProduct.variantId === primaryProductOncart.variantId) +
        1
    );

    const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnAddToCart', {
      detail: {
        value: `cr_recommendationstp!PDP!${storeAndCountryCode}!X!ATC!${projectNamePrimary.current}!${mainProduct.variantId}!${variantIds}!${positions}!${allPrimaryProducts.length}!${slotsComplementaryProducts}`,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };

  const onClickOnProductName = (productClicked, allProducts) => {
    const isPrimaryProduct = allProducts.some((productInArray) => productInArray.variantId === mainProduct.variantId);
    const nameproject = isPrimaryProduct ? projectNamePrimary.current : projectNameSecondary.current;
    const productPosition =
      allProducts.findIndex((productInArray) => productInArray.variantId === productClicked.variantId) -
      (isPrimaryProduct ? 1 : 0);
    const productsLength = allProducts.length - (isPrimaryProduct ? 1 : 0);
    const positionExerience = isPrimaryProduct ? slotsComplementaryProducts : slotsSimilarProducts;

    const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnClickOnProductName', {
      detail: {
        value: `cr_recommendationstp!PDP!${storeAndCountryCode}!X!ATC!${nameproject}!${mainProduct.variantId}!${productClicked.variantId}!${productPosition}!${productsLength}!${positionExerience}`,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };

  const onClickOnExchange = (primaryProduct, secondaryProduct, _allPrimaryProduct, allSecondaryProduct) => {
    const productPosition = allSecondaryProduct.findIndex(
      (productInArray) => productInArray.variantId === secondaryProduct.variantId
    );

    const eventToDigitalData = new CustomEvent('DDPDPShopTheProjectOnClickOnExchange', {
      detail: {
        value: `cr_recommendationstp!PDP!${storeAndCountryCode}!X!X!${projectNameSecondary.current}!${primaryProduct.variantId}!${secondaryProduct.variantId}!${productPosition}!${allSecondaryProduct.length}!${slotsSimilarProducts}`,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };

  const analytics = {
    onRender,
    onClickOnCallToAction,
    onPrimaryProductImpression,
    onSecondaryProductImpression,
    onAddToCart,
    onClickOnProductName,
    onClickOnExchange,
  };

  if (initialPrimaryProducts.length < minComplementaryProducts) return null;
  return (
    <>
      <style jsx>{styles}</style>
      <div className="shop-the-project-container">
        <ShopTheProject
          labels={labels}
          initialShowCoachmark={showCoachmark}
          initialPrimaryProducts={initialPrimaryProducts.slice(0, maxComplementaryProducts)}
          mainProduct={mainProduct}
          addToCart={handleAddToCart}
          getSecondaryProducts={getSecondaryProducts}
          deviceType={appCtx.deviceType}
          analytics={analytics}
        />
      </div>
    </>
  );
};

ShopTheProjectWrapper.propTypes = {
  activeImageIndex: PropTypes.number.isRequired,
  mainProductImage: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
};

export { ShopTheProjectWrapper };
export default withProduct(withApplicationContext(withCart(ShopTheProjectWrapper)));
